import { NotificationAlertErrorShieldSolidIcon } from '@tableau/icons/NotificationAlertErrorShieldSolidIcon';
import { NotificationSuccessIcon } from '@tableau/icons/NotificationSuccessIcon';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';

import { FormikTextBox } from '~commonInfra/components/FormikField';
import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { wideField } from '~src/settings/general-settings/styles/site-name-and-id/SiteNameAndIdStyles';
import { UpdateStringServerSettingNames } from '~src/settings/ServerApi/requests/serverSettings/UpdateStringServerSettingRequest';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';

import { ServerSettingsProps } from '../../server-settings/ReactServerSettings';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';

type ExploreInTableauSectionProps = ServerSettingsProps<'cloudIntegrationTableauServerId' | 'cloudIntegrationInstantAnalyticsApiKey'>;

const MAX_STRING_LENGTH = 255;

function ExploreInTableauSection(props: ExploreInTableauSectionProps) {
  const [configValidationState, setConfigValidationState] = React.useState<boolean | undefined>(undefined); // Add state for validation
  if (!FeatureFlags.instantAnalyticsOnPremEnabled)
    return null;

  const handleVerifyConfig = async () => {  // Create a handler function
    try {
      const verificationResult = await ServerSettingsInstance.verifyCloudIntegrationApiKey(
        UpdateStringServerSettingNames.cloudIntegrationInstantAnalyticsApiKey
      );
      setConfigValidationState(!verificationResult.errors || verificationResult.errors.length === 0);
    } catch(error) {
      setConfigValidationState(false);
    }
  };

  return (
    <SettingsSection
      title={Messages.exploreInTableauHeader()}
      description={Messages.exploreInTableauDescription()}
    >
      <div style={{display: 'inline-flex', alignItems: 'center', marginBottom: '16px'}}>
        <ButtonWidget testId="cloud-integration-instant-analytics-verifty-saved-settings"
          style={{marginRight: '12px'}}
          buttonType={ ButtonType.Outline }
          onClick={handleVerifyConfig}>
          { Messages.exploreInTableauValidateButtonText() }
        </ButtonWidget>
        {configValidationState !== undefined && (configValidationState ?
          <NotificationSuccessIcon size={14} alt={Messages.exploreInTableauConfigured()} /> :
          <NotificationAlertErrorShieldSolidIcon size={14} alt={Messages.exploreInTableauNotConfigured()} />) }
      </div>
      <table>
        <tbody>
          <tr>
            <td>
              <FormikTextBox
                name={'settings.cloudIntegrationTableauServerId'}
                label={Messages.exploreInTableauServerIdLabel()}
                testId={'cloud-integration-tableau-server-id'}
                className={wideField}
                maxLength={MAX_STRING_LENGTH}
              />
            </td>
          </tr>
          <tr>
            <td>
              <FormikTextBox
                name={'settings.cloudIntegrationInstantAnalyticsApiKey'}
                label={Messages.exploreInTableauApiKeyLabel()}
                testId={'cloud-integration-instant-analytics-api-key'}
                className={wideField}
                maxLength={MAX_STRING_LENGTH}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </SettingsSection>);
}

export const ExploreInTableauSpec = componentRegistryEntry({
  name: 'ExploreInTableauSection',
  component: ExploreInTableauSection,
  onSubmit: async (settings, previousServerSettings) => {
    // Verify if settings have changed.
    if (settings.cloudIntegrationTableauServerId === previousServerSettings.cloudIntegrationTableauServerId &&
      settings.cloudIntegrationInstantAnalyticsApiKey === previousServerSettings.cloudIntegrationInstantAnalyticsApiKey)
      return;

    await Promise.all([
      ServerSettingsInstance.updateString(
        UpdateStringServerSettingNames.cloudIntegrationTableauServerId,
        settings.cloudIntegrationTableauServerId),
      ServerSettingsInstance.updateString(
        UpdateStringServerSettingNames.cloudIntegrationInstantAnalyticsApiKey,
        settings.cloudIntegrationInstantAnalyticsApiKey ?? '',
        true) // encrypt the API key
    ]);
  }
});
